import type { ErrorCode } from './generated/zod'

/** Codes the client makes up itself; the API never sends these (Frontend §3.2). */
export type ClientErrorCode = 'network_error' | 'timeout' | 'invalid_response'

/** API.md §1.6 code (the generated `ErrorCode` union) or a client-only code. */
export type ApiErrorCode = ErrorCode | ClientErrorCode

/** `details.issues` on `invalid_request` (API.md §1.6). */
export interface ApiIssue {
  path: (string | number)[]
  code: string
  message: string
}

export interface ApiErrorInit {
  status: number
  code: ApiErrorCode
  safeMessage?: string
  details?: Record<string, unknown>
  issues?: ApiIssue[]
  retryAfterSec?: number
  requestId?: string
}

/**
 * Every failed API call rejects with this. The `message` never holds request or response bodies, so it is safe to
 * log; `safeMessage` is the API's own `message`, which is safe to show.
 */
export class ApiError extends Error {
  /** 0 for network failures and timeouts. */
  readonly status: number
  readonly code: ApiErrorCode
  readonly safeMessage?: string
  readonly details?: Record<string, unknown>
  readonly issues?: ApiIssue[]
  /** `Retry-After`, in seconds (429, 503, some 403 `blocked`). */
  readonly retryAfterSec?: number
  /** `X-Request-Id` of the response, for "Ref: …" in staff error toasts. */
  readonly requestId?: string

  constructor(init: ApiErrorInit) {
    super(`API error: ${init.code} (HTTP ${String(init.status)})`)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    if (init.safeMessage !== undefined) this.safeMessage = init.safeMessage
    if (init.details !== undefined) this.details = init.details
    if (init.issues !== undefined) this.issues = init.issues
    if (init.retryAfterSec !== undefined) this.retryAfterSec = init.retryAfterSec
    if (init.requestId !== undefined) this.requestId = init.requestId
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}
